/**
 * client-theme-build: the ready-made client looks.
 *
 * A studio picks two colors (a color and an accent) plus dark or light and, on
 * dark, glass or solid cards. Everything else on the client app is derived here
 * by one pure function, so a look is never stored as a token set.
 *
 * Source of truth for the values and the math: the signed-off playgrounds
 * docs/mockups/client-themes/defaults/dark-playground.html and
 * light-playground.html (`toOk`, `fromOk`, `shift`, `a`, `trip`, `tokens`,
 * `ACC`, `GROUNDS`), copied verbatim by the confirmed picker mockup
 * docs/mockups/client-themes/picker/client-look-picker.html. The playgrounds set
 * 55 of the vocabulary's tokens; the other 43 follow design D2 of
 * openspec/changes/client-theme-build/design.md.
 *
 * Contract: docs/data-model/dm-client-theming.md. Pure: no DOM, no DB, no env.
 */
import type { ClientThemeTokens } from './tokens.js';
import { DEFAULT_CLIENT_FONT_STACKS } from './fonts.js';

export type LookMode = 'dark' | 'light';
export type LookCards = 'glass' | 'solid';

export type DarkLookColorId =
  | 'rosewood' | 'ember' | 'charcoal' | 'forest' | 'deepharbor' | 'navy' | 'plum' | 'berry';
export type LightLookColorId =
  | 'rosequartz' | 'apricot' | 'stone' | 'sage' | 'seamist' | 'powder' | 'wisteria' | 'petal';
export type DarkLookAccentId =
  | 'rose' | 'coral' | 'peach' | 'honey' | 'mint' | 'seaglass' | 'aqua' | 'sky' | 'lilac' | 'blush';
export type LightLookAccentId =
  | 'garnet' | 'brick' | 'terracotta' | 'ink' | 'emerald' | 'jade' | 'lagoon' | 'cobalt' | 'violet' | 'raspberry';

/** The four facts a studio stores. A light look is always `solid`. */
export interface ClientLook {
  mode: LookMode;
  color: string;
  accent: string;
  cards: LookCards;
}

export interface LookColor {
  id: string;
  name: string;
  /** The ground hex every surface derives from. */
  ground: string;
  /** Exactly five accent ids of this mode, in order; the first is the default. */
  accents: readonly string[];
}

export interface LookAccent {
  name: string;
  /** null = derived from the color (Ink: a near-black tinted to the ground's hue). */
  hex: string | null;
}

/**
 * Dark accents are the soft set; light accents are their deep partners. The two
 * maps share slot order (rose ↔ garnet, coral ↔ brick, …), so a dark color and
 * its light twin list their accents in the same slots.
 */
export const LOOK_ACCENTS: {
  dark: Record<DarkLookAccentId, LookAccent>;
  light: Record<LightLookAccentId, LookAccent>;
} = {
  dark: {
    rose: { name: 'Rose', hex: '#F4A4AD' },
    coral: { name: 'Coral', hex: '#F8A189' },
    peach: { name: 'Peach', hex: '#EDB89A' },
    honey: { name: 'Honey', hex: '#EBCA80' },
    mint: { name: 'Mint', hex: '#98DDB4' },
    seaglass: { name: 'Sea glass', hex: '#8FD3CA' },
    aqua: { name: 'Aqua', hex: '#7ED4DE' },
    sky: { name: 'Sky', hex: '#90C8F4' },
    lilac: { name: 'Lilac', hex: '#C4B2F1' },
    blush: { name: 'Blush', hex: '#EFAECC' },
  },
  light: {
    garnet: { name: 'Garnet', hex: '#AA4859' },
    brick: { name: 'Brick', hex: '#B04426' },
    terracotta: { name: 'Terracotta', hex: '#9B5528' },
    ink: { name: 'Ink', hex: null },
    emerald: { name: 'Emerald', hex: '#06784A' },
    jade: { name: 'Jade', hex: '#10746B' },
    lagoon: { name: 'Lagoon', hex: '#0B727C' },
    cobalt: { name: 'Cobalt', hex: '#166DA4' },
    violet: { name: 'Violet', hex: '#745AA8' },
    raspberry: { name: 'Raspberry', hex: '#9B4D75' },
  },
};

/** Eight dark colors and their eight light twins, in the same order. */
export const LOOK_COLORS: { dark: readonly LookColor[]; light: readonly LookColor[] } = {
  dark: [
    { id: 'rosewood', name: 'Rosewood', ground: '#4A3032', accents: ['honey', 'rose', 'peach', 'seaglass', 'sky'] },
    { id: 'ember', name: 'Ember', ground: '#483226', accents: ['peach', 'honey', 'sky', 'mint', 'rose'] },
    { id: 'charcoal', name: 'Charcoal', ground: '#35383C', accents: ['honey', 'coral', 'mint', 'sky', 'lilac'] },
    { id: 'forest', name: 'Forest', ground: '#2A3E2D', accents: ['peach', 'mint', 'honey', 'blush', 'lilac'] },
    { id: 'deepharbor', name: 'Deep harbor', ground: '#1F3D43', accents: ['peach', 'seaglass', 'honey', 'coral', 'blush'] },
    { id: 'navy', name: 'Navy', ground: '#2B394C', accents: ['honey', 'sky', 'peach', 'coral', 'mint'] },
    { id: 'plum', name: 'Plum', ground: '#3B3449', accents: ['blush', 'lilac', 'honey', 'mint', 'peach'] },
    { id: 'berry', name: 'Berry', ground: '#46303C', accents: ['mint', 'blush', 'honey', 'peach', 'sky'] },
  ],
  light: [
    { id: 'rosequartz', name: 'Rose quartz', ground: '#FCE5E5', accents: ['ink', 'garnet', 'terracotta', 'jade', 'cobalt'] },
    { id: 'apricot', name: 'Apricot', ground: '#FBE7DC', accents: ['terracotta', 'ink', 'cobalt', 'emerald', 'garnet'] },
    { id: 'stone', name: 'Stone', ground: '#EEEAE5', accents: ['ink', 'brick', 'emerald', 'cobalt', 'violet'] },
    { id: 'sage', name: 'Sage', ground: '#E0F0E2', accents: ['terracotta', 'emerald', 'ink', 'raspberry', 'violet'] },
    { id: 'seamist', name: 'Sea mist', ground: '#D8F0F5', accents: ['terracotta', 'jade', 'ink', 'brick', 'raspberry'] },
    { id: 'powder', name: 'Powder', ground: '#E0ECFD', accents: ['ink', 'cobalt', 'terracotta', 'brick', 'emerald'] },
    { id: 'wisteria', name: 'Wisteria', ground: '#EDE8FB', accents: ['raspberry', 'violet', 'ink', 'emerald', 'terracotta'] },
    { id: 'petal', name: 'Petal', ground: '#F9E5EF', accents: ['emerald', 'raspberry', 'ink', 'terracotta', 'cobalt'] },
  ],
};

/** The recommended look a new studio starts on (design D7). */
export const DEFAULT_CLIENT_LOOK: ClientLook = Object.freeze({
  mode: 'dark',
  color: 'charcoal',
  accent: 'honey',
  cards: 'glass',
}) as ClientLook;

const LOOK_KEYS = ['mode', 'color', 'accent', 'cards'] as const;

export function findLookColor(mode: LookMode, colorId: string): LookColor | undefined {
  return LOOK_COLORS[mode]?.find((c) => c.id === colorId);
}

function findLookAccent(mode: LookMode, accentId: string): LookAccent | undefined {
  const table = LOOK_ACCENTS[mode] as Record<string, LookAccent> | undefined;
  return table && Object.prototype.hasOwnProperty.call(table, accentId) ? table[accentId] : undefined;
}

/**
 * The semantic rule for a stored look: a plain object with exactly the four
 * facts, a known mode, a color of that mode, an accent that color lists, and
 * glass or solid cards with solid on every light look. Anything else is invalid.
 */
export function isClientLook(value: unknown): value is ClientLook {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const keys = Object.keys(value);
  if (keys.length !== LOOK_KEYS.length || !LOOK_KEYS.every((k) => keys.includes(k))) return false;
  const v = value as Record<string, unknown>;
  if (v.mode !== 'dark' && v.mode !== 'light') return false;
  if (typeof v.color !== 'string' || typeof v.accent !== 'string') return false;
  if (v.cards !== 'glass' && v.cards !== 'solid') return false;
  if (v.mode === 'light' && v.cards !== 'solid') return false;
  const color = findLookColor(v.mode, v.color);
  return color !== undefined && color.accents.includes(v.accent);
}

/** "Deep harbor · Peach · Glass cards", "Sea mist · Terracotta · Light". */
export function lookName(look: ClientLook): string {
  const color = findLookColor(look.mode, look.color);
  const accent = findLookAccent(look.mode, look.accent);
  const tail = look.mode === 'dark' ? (look.cards === 'glass' ? 'Glass cards' : 'Solid cards') : 'Light';
  return `${color?.name ?? look.color} · ${accent?.name ?? look.accent} · ${tail}`;
}

// ── OKLCH helpers, ported verbatim from the playgrounds ──
const toLin = (c: number): number => {
  c /= 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};
const fromLin = (c: number): number => {
  const v = c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
  return Math.round(Math.min(1, Math.max(0, v)) * 255);
};
function toOk(h: string): [number, number, number] {
  const [r, g, b] = [1, 3, 5].map((i) => toLin(parseInt(h.slice(i, i + 2), 16)));
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b),
    m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b),
    s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s;
  return [L, Math.hypot(A, B), ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360];
}
function fromOk(L: number, C: number, H: number): string {
  const A = C * Math.cos((H * Math.PI) / 180),
    B = C * Math.sin((H * Math.PI) / 180);
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3,
    m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3,
    s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
  const r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    b = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;
  return '#' + [r, g, b].map(fromLin).map((v) => v.toString(16).padStart(2, '0')).join('').toUpperCase();
}
const shift = (h: string, dL: number, kC = 1): string => {
  const [L, C, H] = toOk(h);
  return fromOk(Math.min(0.98, Math.max(0, L + dL)), C * kC, H);
};
const hex3 = (h: string): number[] => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
const trip = (h: string): string => hex3(h).join(', ');
const a = (h: string, al: number): string => `rgba(${trip(h)}, ${al})`;

/** The accent hex a look paints with; Ink is derived from the color's own hue. */
export function lookAccentHex(mode: LookMode, colorId: string, accentId: string): string | null {
  const accent = findLookAccent(mode, accentId);
  if (!accent) return null;
  if (accent.hex !== null) return accent.hex;
  const color = findLookColor(mode, colorId);
  return color ? fromOk(0.3, 0.035, toOk(color.ground)[2]) : null;
}

type PlaygroundTokens = Record<string, string>;

/* The dark playground's `tokens(g, acc, cards)`, verbatim except the non-vocabulary
   font and radius entries, which the client CSS owns. */
function darkPlaygroundTokens(g: string, acc: string, cards: LookCards): PlaygroundTokens {
  const [, , gh] = toOk(g);
  const card = shift(g, 0.045), card2 = shift(g, 0.075), bookCard = shift(g, 0.06), glassCard = shift(g, 0.08);
  const text = fromOk(0.955, 0.008, gh);
  const lite = shift(acc, 0.08, 0.8), deep = shift(acc, -0.08, 1.05);
  const shadow = '0 10px 26px rgba(0, 0, 0, 0.32)';
  const surface = cards === 'glass' ? {
    '--glass-bg-strong': a(glassCard, 0.62), '--glass-bg': a(glassCard, 0.55), '--glass-blur': 'blur(20px) saturate(130%)', '--glass-border-strong': a(text, 0.2),
    '--booking-card-ring-bg': `linear-gradient(135deg, ${a(text, 0.3)} 0%, ${a(text, 0.05)} 100%)`,
    '--book-card-bg': a(glassCard, 0.62),
    '--quick-action-1-bg': a(glassCard, 0.55), '--quick-action-2-bg': a(glassCard, 0.55), '--quick-action-3-bg': a(glassCard, 0.55),
    '--bottom-nav-bg': a(card, 0.72),
  } : {
    '--glass-bg-strong': card, '--glass-bg': card, '--glass-blur': 'blur(0px)', '--glass-border-strong': a(text, 0.14),
    '--booking-card-ring-bg': `linear-gradient(135deg, ${a(text, 0.16)} 0%, ${a(text, 0.03)} 100%)`,
    '--book-card-bg': bookCard,
    '--quick-action-1-bg': card, '--quick-action-2-bg': card, '--quick-action-3-bg': card,
    '--bottom-nav-bg': a(card, 0.97),
  };
  return {
    '--bg-page': g, '--bg-card': card, '--bg-card-2': card2,
    '--text-primary': text, '--text-secondary': a(text, 0.74), '--text-tertiary': a(text, 0.56), '--text-on-accent': g,
    '--accent': acc, '--accent-hot': shift(acc, 0.05), '--accent-deep': acc, '--accent-rgb': trip(acc),
    '--session-icon-bg': `radial-gradient(circle at 30% 28%, ${a(acc, 0.28)} 0%, ${a(acc, 0.1)} 100%)`, '--session-icon-border': a(acc, 0.3), '--session-icon-glyph': acc,
    '--status-pill-bg': a(text, 0.1), '--status-pill-text': a(text, 0.86),
    '--book-icon-bg': `radial-gradient(circle at 30% 28%, ${lite} 0%, ${acc} 55%, ${deep} 100%)`, '--book-icon-border': 'none', '--book-icon-shadow': `0 0 22px ${a(acc, 0.25)}`,
    '--book-card-ring-bg': a(acc, 0.32), '--book-card-bloom-bg': `radial-gradient(ellipse 70% 90% at 0% 0%, ${a(acc, 0.16)} 0%, ${a(acc, 0)} 100%)`,
    '--book-arrow-bg': a(g, 0.5),
    '--capacity-block-bg': 'rgba(0, 0, 0, 0.2)', '--capacity-block-border': 'rgba(255, 255, 255, 0.05)', '--capacity-block-shadow': 'none', '--capacity-track-bg': 'rgba(255, 255, 255, 0.1)', '--capacity-fill-bg': acc,
    '--spots-pill-bg': text, '--spots-pill-text': g, '--spots-pill-low-bg': acc,
    '--quick-action-icon-color': acc,
    '--nav-fab-bg': `radial-gradient(circle at 30% 28%, ${lite} 0%, ${acc} 55%, ${deep} 100%)`, '--nav-fab-shadow': '0 6px 18px rgba(0, 0, 0, 0.4)',
    '--status-glyph-color': '#FFFFFF', '--tg-control-bg': 'rgba(255, 255, 255, 0.1)', '--home-indicator-bg': 'rgba(255, 255, 255, 0.32)', '--chevron-color': 'rgba(255, 255, 255, 0.45)',
    '--glass-shadow': shadow, '--book-card-shadow': shadow, '--bottom-nav-shadow': '0 14px 40px rgba(0, 0, 0, 0.45)',
    '--booking-card-shadow': shadow, '--happening-card-shadow': shadow, '--about-card-shadow': shadow,
    ...surface,
    '--page-image': 'none', '--page-overlay': 'none',
  };
}

/* The light playground's `tokens(g, acc, cards)`, verbatim except the non-vocabulary
   font and radius entries. Light looks are stored solid; the glass branch stays as
   ported so the math reads one to one with the playground. */
function lightPlaygroundTokens(g: string, acc: string, cards: LookCards): PlaygroundTokens {
  const [, , gh] = toOk(g);
  const text = fromOk(0.27, 0.035, gh);
  const white = fromOk(0.99, 0.004, gh), bookCard = fromOk(0.975, 0.012, gh), card2 = shift(g, -0.03);
  const lite = shift(acc, 0.1, 0.9), deep = shift(acc, -0.07, 1.05);
  const shadow = `0 8px 22px ${a(text, 0.1)}`;
  const surface = cards === 'glass' ? {
    '--glass-bg-strong': a(white, 0.66), '--glass-bg': a(white, 0.58), '--glass-blur': 'blur(20px) saturate(140%)', '--glass-border-strong': a(white, 0.9),
    '--booking-card-ring-bg': `linear-gradient(135deg, ${a(white, 0.95)} 0%, ${a(white, 0.35)} 100%)`,
    '--book-card-bg': a(white, 0.7),
    '--quick-action-1-bg': a(white, 0.58), '--quick-action-2-bg': a(white, 0.58), '--quick-action-3-bg': a(white, 0.58),
    '--bottom-nav-bg': a(white, 0.78),
  } : {
    '--glass-bg-strong': white, '--glass-bg': white, '--glass-blur': 'blur(0px)', '--glass-border-strong': a(text, 0.1),
    '--booking-card-ring-bg': `linear-gradient(135deg, ${a(text, 0.1)} 0%, ${a(text, 0.03)} 100%)`,
    '--book-card-bg': bookCard,
    '--quick-action-1-bg': white, '--quick-action-2-bg': white, '--quick-action-3-bg': white,
    '--bottom-nav-bg': a(white, 0.97),
  };
  return {
    '--bg-page': g, '--bg-card': white, '--bg-card-2': card2,
    '--text-primary': text, '--text-secondary': a(text, 0.78), '--text-tertiary': a(text, 0.62), '--text-on-accent': '#FFFFFF',
    // Hot is the readable accent for small text: on a light ground that means a deeper step, not the
    // playground's shift(acc, .05), which read 3.74:1 at worst (Gabriel 2026-10-09, accent audit).
    '--accent': acc, '--accent-hot': shift(acc, -0.02, 1.05), '--accent-deep': deep, '--accent-rgb': trip(acc),
    '--session-icon-bg': `radial-gradient(circle at 30% 28%, ${a(acc, 0.18)} 0%, ${a(acc, 0.07)} 100%)`, '--session-icon-border': a(acc, 0.25), '--session-icon-glyph': acc,
    '--status-pill-bg': a(text, 0.07), '--status-pill-text': text,
    '--book-icon-bg': `radial-gradient(circle at 30% 28%, ${lite} 0%, ${acc} 55%, ${deep} 100%)`, '--book-icon-border': 'none', '--book-icon-shadow': `0 6px 16px ${a(acc, 0.3)}`,
    '--book-card-ring-bg': a(acc, 0.28), '--book-card-bloom-bg': `radial-gradient(ellipse 70% 90% at 0% 0%, ${a(acc, 0.12)} 0%, ${a(acc, 0)} 100%)`,
    '--book-arrow-bg': a('#FFFFFF', 0.6),
    '--capacity-block-bg': a(text, 0.04), '--capacity-block-border': a(text, 0.06), '--capacity-block-shadow': 'none', '--capacity-track-bg': a(text, 0.1), '--capacity-fill-bg': acc,
    '--spots-pill-bg': text, '--spots-pill-text': '#FFFFFF', '--spots-pill-low-bg': acc,
    '--quick-action-icon-color': acc,
    '--nav-fab-bg': `radial-gradient(circle at 30% 28%, ${lite} 0%, ${acc} 55%, ${deep} 100%)`, '--nav-fab-shadow': `0 6px 18px ${a(acc, 0.35)}`,
    '--status-glyph-color': text, '--tg-control-bg': a(text, 0.07), '--home-indicator-bg': a(text, 0.26), '--chevron-color': a(text, 0.42),
    '--glass-shadow': shadow, '--book-card-shadow': shadow, '--bottom-nav-shadow': `0 12px 32px ${a(text, 0.14)}`,
    '--booking-card-shadow': shadow, '--happening-card-shadow': shadow, '--about-card-shadow': shadow,
    ...surface,
    '--page-image': 'none', '--page-overlay': 'none',
  };
}

/**
 * The complete client token set for a look: the playground's 55 tokens plus the
 * 43 design D2 derives (surfaces, the accent ramp, brand-2, danger, text-muted,
 * borders, glows, shadows, avatar, catalog) and today's two font stacks. Call it with a look that passes
 * `isClientLook`; it throws on an unknown color or accent so a caller can never
 * paint a half-derived set.
 */
export function buildLookTokens(look: ClientLook): ClientThemeTokens {
  const color = findLookColor(look.mode, look.color);
  const acc = lookAccentHex(look.mode, look.color, look.accent);
  if (!color || !acc) {
    throw new Error(`Unknown client look: ${look.mode}/${look.color}/${look.accent}`);
  }
  const dark = look.mode === 'dark';
  const g = color.ground;
  const base = dark ? darkPlaygroundTokens(g, acc, look.cards) : lightPlaygroundTokens(g, acc, look.cards);
  const text = base['--text-primary'];

  const hot = base['--accent-hot'];
  const deep = base['--accent-deep'];
  const light = shift(acc, 0.1, 0.85);
  const shadowTone = shift(acc, -0.16, 1.05);
  const danger = dark ? '#E5484D' : '#C8373C';
  // The accent buttons (orb) and stripe, lit at the top. Dark looks paint them light to
  // deep with the nav fab's own tones, under ground-colored words (Gabriel 2026-10-09,
  // docs/mockups/client-themes/accent-buttons, option C): the playground's dark
  // --accent-deep is the accent itself, which left the orb a flat block. Light looks keep
  // the playground's lit top, shift(acc, .05), now that their --accent-hot is deeper.
  const orbTop = dark ? shift(acc, 0.08, 0.8) : shift(acc, 0.05);
  const accentRamp = dark
    ? `linear-gradient(to bottom, ${orbTop} 0%, ${acc} 50%, ${shift(acc, -0.08, 1.05)} 100%)`
    : null;

  const derived = {
    '--bg-page-rgb': trip(g),
    '--fg-page-rgb': trip(text),
    '--bg-vignette': dark ? shift(g, -0.03) : g,
    '--bg-inset': dark ? 'rgba(0, 0, 0, 0.25)' : a(text, 0.04),
    '--bg-glass': base['--glass-bg'],
    '--bg-card-warm': base['--bg-card'],
    '--glass-border': a(text, dark ? 0.08 : 0.06),
    '--glass-glow': 'transparent',
    '--accent-hot-rgb': trip(hot),
    '--accent-light': light,
    '--accent-light-rgb': trip(light),
    '--accent-deep-rgb': trip(deep),
    '--accent-shadow': shadowTone,
    '--accent-shadow-rgb': trip(shadowTone),
    '--accent-stripe': accentRamp ?? `linear-gradient(to bottom, ${orbTop} 0%, ${acc} 50%, ${shadowTone} 100%)`,
    '--accent-orb': accentRamp ?? `linear-gradient(to bottom, ${orbTop} 0%, ${acc} 50%, ${deep} 100%)`,
    '--brand-2': acc,
    '--brand-2-rgb': trip(acc),
    '--brand-2-hot': hot,
    '--brand-2-hot-rgb': trip(hot),
    '--brand-2-light': light,
    '--brand-2-deep': deep,
    '--brand-2-deep-rgb': trip(deep),
    '--brand-2-shadow': shadowTone,
    '--danger': danger,
    '--danger-rgb': trip(danger),
    '--text-muted': a(text, dark ? 0.4 : 0.5),
    '--border-warm': a(acc, 0.16),
    '--border-warm-strong': a(acc, 0.32),
    '--border-subtle': a(text, 0.06),
    '--border-soft': a(text, 0.1),
    '--glow-amber': `0 0 32px ${a(acc, 0.28)}`,
    '--glow-amber-soft': `0 0 18px ${a(acc, 0.14)}`,
    '--shadow-card': dark ? '0 12px 36px rgba(0, 0, 0, 0.55)' : `0 12px 36px ${a(text, 0.12)}`,
    '--shadow-fab': base['--nav-fab-shadow'],
    '--avatar-bg': base['--book-icon-bg'],
    '--avatar-shadow': dark ? '0 3px 10px rgba(0, 0, 0, 0.35)' : `0 3px 10px ${a(text, 0.15)}`,
    '--avatar-initials-color': base['--text-on-accent'],
    // Every ring that drew the playground's white fade takes the accent at Book a
    // Session's strength (Gabriel 2026-10-22: "whatever was using that shared white
    // ring should be shifted to accent"); this overrides the playground's value.
    '--booking-card-ring-bg': base['--book-card-ring-bg'],
    '--catalog-card-ring-bg': base['--book-card-ring-bg'],
    '--catalog-tab-ring-bg': base['--book-card-ring-bg'],
    '--event-date-color': acc,
    '--spots-low-color': acc,
    '--price-free-color': acc,
    // tenant-font-choice: a look never picks fonts; it carries today's, and a studio's
    // font overrides layer on top like any other.
    ...DEFAULT_CLIENT_FONT_STACKS,
  };

  return { ...base, ...derived } as ClientThemeTokens;
}
